import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, RefreshCwIcon, CalendarIcon, DownloadIcon, FileTextIcon, EyeIcon } from 'lucide-react';
import { Payment, PaymentStatus } from './PaymentsView';
import { formatCurrency } from '../../utils/currencyFormatter';
interface PaymentsListProps {
  payments: Payment[];
  onSelectPayment: (payment: Payment) => void;
  locale: string;
}
export function PaymentsList({
  payments,
  onSelectPayment,
  locale
}: PaymentsListProps) {
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>(payments);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    invoiceNumber: '',
    vendorName: '',
    dateFrom: '',
    dateTo: '',
    entitySite: '',
    status: '',
    searchTerm: ''
  });
  // Apply filters when they change or payments change
  useEffect(() => {
    let result = [...payments];
    // Apply search term filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(payment => payment.paymentReference.toLowerCase().includes(term) || payment.linkedInvoices.some(invoice => invoice.invoiceNumber.toLowerCase().includes(term)) || payment.linkedInvoices.some(invoice => invoice.vendorName.toLowerCase().includes(term)) || payment.externalSourceId?.toLowerCase().includes(term));
    }
    // Apply specific filters
    if (filters.invoiceNumber) {
      result = result.filter(payment => payment.linkedInvoices.some(invoice => invoice.invoiceNumber.toLowerCase().includes(filters.invoiceNumber.toLowerCase())));
    }
    if (filters.vendorName) {
      result = result.filter(payment => payment.linkedInvoices.some(invoice => invoice.vendorName.toLowerCase().includes(filters.vendorName.toLowerCase())));
    }
    if (filters.dateFrom) {
      result = result.filter(payment => new Date(payment.paymentDate) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter(payment => new Date(payment.paymentDate) <= new Date(filters.dateTo));
    }
    if (filters.entitySite) {
      result = result.filter(payment => payment.entityName.toLowerCase().includes(filters.entitySite.toLowerCase()) || payment.siteName?.toLowerCase().includes(filters.entitySite.toLowerCase()));
    }
    if (filters.status) {
      result = result.filter(payment => payment.status === filters.status);
    }
    setFilteredPayments(result);
  }, [payments, filters]);
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const clearFilters = () => {
    setFilters({
      invoiceNumber: '',
      vendorName: '',
      dateFrom: '',
      dateTo: '',
      entitySite: '',
      status: '',
      searchTerm: ''
    });
  };
  const handleExportCSV = () => {
    // In a real app, this would trigger a CSV export
    alert('Exporting payments to CSV...');
  };
  const handleExportPDF = () => {
    // In a real app, this would trigger a PDF export
    alert('Exporting payments to PDF...');
  };
  // Get unique statuses for filter dropdown
  const statuses = Array.from(new Set(payments.map(payment => payment.status)));
  // Helper function to display multiple invoice numbers
  const formatInvoiceNumbers = (payment: Payment) => {
    if (payment.linkedInvoices.length === 1) {
      return payment.linkedInvoices[0].invoiceNumber;
    }
    return `${payment.linkedInvoices[0].invoiceNumber} +${payment.linkedInvoices.length - 1} more`;
  };
  // Helper function to display vendor names
  const formatVendorNames = (payment: Payment) => {
    // Check if all invoices are from the same vendor
    const uniqueVendors = new Set(payment.linkedInvoices.map(inv => inv.vendorId));
    if (uniqueVendors.size === 1) {
      return payment.linkedInvoices[0].vendorName;
    }
    return `Multiple vendors (${uniqueVendors.size})`;
  };
  return <div className="bg-white shadow rounded-lg" data-id="element-8484">
      {/* Header and Search */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-8485">
        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-8486">
          Payment Records
        </h3>
        <div className="flex items-center space-x-2" data-id="element-8487">
          <div className="relative rounded-md shadow-sm" data-id="element-8488">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8489">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-8490" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search payments..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-8491" />
          </div>
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8492">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-8493" />
            Filters
          </button>
          <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8494">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-8495" />
            Reset
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {isFilterOpen && <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-8496">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-8497">
            <div className="sm:col-span-2" data-id="element-8498">
              <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-8499">
                Invoice Number
              </label>
              <input type="text" id="invoiceNumber" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.invoiceNumber} onChange={e => handleFilterChange('invoiceNumber', e.target.value)} data-id="element-8500" />
            </div>
            <div className="sm:col-span-2" data-id="element-8501">
              <label htmlFor="vendorName" className="block text-sm font-medium text-gray-700" data-id="element-8502">
                Vendor Name
              </label>
              <input type="text" id="vendorName" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.vendorName} onChange={e => handleFilterChange('vendorName', e.target.value)} data-id="element-8503" />
            </div>
            <div className="sm:col-span-2" data-id="element-8504">
              <label htmlFor="entitySite" className="block text-sm font-medium text-gray-700" data-id="element-8505">
                Entity / Site
              </label>
              <input type="text" id="entitySite" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.entitySite} onChange={e => handleFilterChange('entitySite', e.target.value)} data-id="element-8506" />
            </div>
            <div className="sm:col-span-2" data-id="element-8507">
              <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-8508">
                Payment Date From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8509">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8510">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-8511" />
                </div>
                <input type="date" id="dateFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateFrom} onChange={e => handleFilterChange('dateFrom', e.target.value)} data-id="element-8512" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-8513">
              <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-8514">
                Payment Date To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8515">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8516">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-8517" />
                </div>
                <input type="date" id="dateTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateTo} onChange={e => handleFilterChange('dateTo', e.target.value)} data-id="element-8518" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-8519">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-8520">
                Status
              </label>
              <select id="status" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-8521">
                <option value="" data-id="element-8522">All Statuses</option>
                {statuses.map(status => <option key={status} value={status} data-id="element-8523">
                    {status}
                  </option>)}
              </select>
            </div>
          </div>
        </div>}

      {/* Export Buttons */}
      <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-2" data-id="element-8524">
        <button onClick={handleExportCSV} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8525">
          <DownloadIcon className="h-4 w-4 mr-1" data-id="element-8526" />
          Export CSV
        </button>
        <button onClick={handleExportPDF} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8527">
          <FileTextIcon className="h-4 w-4 mr-1" data-id="element-8528" />
          Export PDF
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-8529">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-8530">
          <thead className="bg-gray-50" data-id="element-8531">
            <tr data-id="element-8532">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8533">
                Invoice(s)
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8534">
                Vendor
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8535">
                Payment Reference
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8536">
                Payment Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8537">
                Payment Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8538">
                Currency
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8539">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8540">
                Entity / Site
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8541">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-8542">
            {filteredPayments.length > 0 ? filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-8543">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-8544">
                    {formatInvoiceNumbers(payment)}
                    {payment.linkedInvoices.length > 1 && <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-8545">
                        {payment.linkedInvoices.length}
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8546">
                    {formatVendorNames(payment)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8547">
                    {payment.paymentReference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8548">
                    {new Date(payment.paymentDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8549">
                    {formatCurrency(payment.amount, payment.currency, locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8550">
                    {payment.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8551">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${payment.status === 'Cleared' ? 'bg-green-100 text-green-800' : payment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : payment.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-8552">
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8553">
                    {payment.entityName}
                    {payment.siteName && ` / ${payment.siteName}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8554">
                    <button onClick={() => onSelectPayment(payment)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" data-id="element-8555">
                      <EyeIcon className="h-4 w-4 mr-1" data-id="element-8556" />
                      View
                    </button>
                  </td>
                </tr>) : <tr data-id="element-8557">
                <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-8558">
                  No payments found matching the current filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination (simplified) */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-8559">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-8560">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-8561">
            Previous
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-8562">
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-8563">
          <div data-id="element-8564">
            <p className="text-sm text-gray-700" data-id="element-8565">
              Showing <span className="font-medium" data-id="element-8566">1</span> to{' '}
              <span className="font-medium" data-id="element-8567">{filteredPayments.length}</span> of{' '}
              <span className="font-medium" data-id="element-8568">{filteredPayments.length}</span>{' '}
              results
            </p>
          </div>
          <div data-id="element-8569">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-8570">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-8571">
                <span className="sr-only" data-id="element-8572">Previous</span>
                &laquo;
              </button>
              <button aria-current="page" className="z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium" data-id="element-8573">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-8574">
                <span className="sr-only" data-id="element-8575">Next</span>
                &raquo;
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}